import { useState, useRef, useEffect, useCallback } from 'react';
import { useImmer } from 'use-immer'
import { Sidebar, SidebarButton, SidebarSpacing, Header, CircularMenuButton, Scrollable, Pagination, useIsFormSubmitting, useHandleSubmitError, InlineHeader, InlineSection, SidebarHeaderProvider } from '@/components/DashboardComponents'
import { useNavigate, useLoaderData, Form, useSubmit, useParams, useRouteLoaderData, useSearchParams, useActionData } from 'react-router'
import { Button, Textbox, Spacing, ThreeQuarterSpacing, RadioGroup, RadioOption, Checkbox, TextboxList, Dropdown, DropdownOption, HalfSpacing, RoundIconButton, FileLocation, InterventionRating } from '@/form/FormComponents'
import { Icon } from '@/components/Icon'
import { autofillScenario, autofillScenarioAddItem, generateRandomNames } from '@/data/ScenarioData'
import { FormProvider, FormObjectGroup, FormArrayGroup, FormDisabled, FormDataProvider, FormSubmitProvider, useFormSubmit, useFormData } from '@/form/FormProvider';
import { Modal, ModalContent, ModalButtons, useModal, useErrorModal, ModalListItem } from '@/components/Modal';
import Lottie from "lottie-react";
import sparklesAnimation from '@/../assets/icons8-sparkles.json';
import races from '@/constants/races'
import voices from '@/constants/voices'
import { shuffle, convertAgeToYears, compareRubricArrays } from '@/shared/utils'
import { generateMeasurements } from '@/constants/generateMeasurements'
import { FilePicker } from '@/form/FilePicker';
import { RubricEditor } from '@/pages/RubricLayout';
import { useQuery } from '@tanstack/react-query'
import { publicAction } from '@/data/loaders'
import { useMenu } from '@/components/Menu';
import { Howl } from 'howler';
import { useFilePickerModal } from '@/components/FilePickerModals';
import { RubricEditorTableThumbnail, convertSectionsToRows } from '@/pages/RubricLayout';
import { useSharedWithMe } from '@/pages/Filesystem';
import { Spinner } from '@/components/Spinner';
import { useShareRubricsModal } from '@/components/FilesystemShareModals';
import { useHandleSharePromptActionData } from '@/pages/NewAssessmentLayout';
import { usePreference } from '@/hooks/useStorage';
import { useUser } from '@/systems/Authentication';

export function NewScenarioLayout() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("basic_details");
  const [in_rubric_editor, setInRubricEditor] = useState(false);
  const scenario = useLoaderData().data?.value;
  const { scenario_id, assessment_id, course_id } = useParams();
  const [searchParams] = useSearchParams();
  const assessment_id_param = searchParams.get("assessment_id");
  // const course_id = searchParams.get("course_id");
  const initial_data = scenario?.data;
  const files = scenario?.files;
  const [initial_rubrics, updateInitialRubrics] = useImmer(scenario?.rubrics || []);
  const [rubrics, updateRubrics] = useImmer(initial_rubrics);
  const [has_autofilled, updateHasAutofilled] = useImmer(initial_data ? { opening_blurb: true, review_of_systems: true, physical_exam: true, diagnostics: true, interventions: true } : {});
  const [tabs_pending_autofill, updateTabsPendingAutofill] = useImmer({});
  const submit = useSubmit();
  const form = useRef();
  const submitting = useIsFormSubmitting();
  useHandleSubmitError();
  const actionData = useActionData();
  const [seenMissingRubricWarning, setSeenMissingRubricWarning] = usePreference("seen_missing_rubric_warning", false);

  const { userdata } = useUser();
  const interventions_enabled = userdata.email?.endsWith("@sapientclinician.com") || initial_data?.allow_interventions;

  const [openUpdateFromLinkedAssessmentsChoiceModal] = useModal(({ context, closeModal }) => {
    const submitForm = ({ formatted_data }) => {
      context.callback({ update_assessment_only: formatted_data.update === "no" });
      closeModal();
    }

    return (
      <Modal title="Update the original scenario file?">
        <FormProvider onSubmit={submitForm}>
          <FormSubmitProvider>
            {({handleSubmit}) => (
              <>
                <ModalContent>
                  <RadioGroup name="update" required="Please choose an option" default_value="yes">
                    <RadioOption name="yes" text="Yes, update the original scenario file"/>
                    <RadioOption name="no" text="No, update just for this assessment"/>
                  </RadioGroup>
                </ModalContent>
                <ModalButtons>
                  <Button text="Cancel" className="hollow" onClick={closeModal}/>
                  <Button text="Continue" className="primary" onClick={handleSubmit}/>
                </ModalButtons>
              </>
            )}
          </FormSubmitProvider>
        </FormProvider>
      </Modal>
    )
  })

  const [openNoRubricScenarioWarningModal] = useModal(({ context, closeModal }) => {
    function submitForm({ formatted_data }) {
      if (formatted_data.dont_show_again) {
        setSeenMissingRubricWarning(true);
      }
      closeModal();
      context.callback();
    }

    return (
      <Modal title="Continue without a rubric?">
        <FormProvider onSubmit={submitForm}>
          <FormSubmitProvider>
            {({handleSubmit}) => (
              <>
                <ModalContent>
                  <div>{`A rubric is needed to generate AI feedback at the end of the scenario. Without one, the learner won't get any feedback.`}</div>
                  <Spacing/>
                  <div>{`You can always add a rubric later when you assign this scenario — this is just a heads up.`}</div>
                  <Spacing/>
                  <Checkbox text={"Don't show this warning again"} name="dont_show_again" default_value={true}/>
                </ModalContent>
                <ModalButtons>
                  <Button text="Go back" className="hollow" onClick={closeModal}/>
                  <Button text="Continue anyway" className="primary" onClick={handleSubmit}/>
                </ModalButtons>
              </>
            )}
          </FormSubmitProvider>
        </FormProvider>
      </Modal>
    )
  })

  useHandleSharePromptActionData({ actionData, course_id, submit });

  const [openShareRubricsModal] = useShareRubricsModal({ wording: "scenario_editor" });
  const lastOpenShareRubricsModal = useRef(null);

  useEffect(() => {
    if (actionData?.share_prompt_rubrics && actionData.date !== lastOpenShareRubricsModal.current) {
      lastOpenShareRubricsModal.current = actionData.date;
      openShareRubricsModal({
        share_prompt_rubrics: actionData.share_prompt_rubrics,
        callback: ({ set_should_share_rubrics, closeModal, setLoading }) => {

          const needs_to_share = set_should_share_rubrics.filter(r => r.should_share);

          if (needs_to_share.length > 0) {

            setLoading(true);

            publicAction("shareFiles", {
              ids: needs_to_share.map(rubric => rubric.file_id),
              people: actionData.people_missing_access,
              level: "edit",
              notify_people: false,
              message: null
            }).then(function() {
        
              closeModal();
              doSubmit();
        
            }).catch(function(error) {
              console.error(error);
              setLoading(false);
              doSubmit();
            })

          } else {
            closeModal();
            doSubmit();
          }

          function doSubmit() {
            submit({ ...actionData.request_data, set_should_share_rubrics: set_should_share_rubrics.map(s => ({ _id: s._id, should_share: s.should_share })) }, { method: "post", encType: "application/json" });
          }
        }
      })
    }
  }, [actionData, openShareRubricsModal, submit]);

  function submitForm({ formatted_data, updateData, passthrough_args }) {
    if (passthrough_args?.part) {
      doAutofill({part: passthrough_args.part, formatted_data, updateData});
      return;
    }
    if (!has_autofilled.opening_blurb) {
      doAutofill({part: "opening_blurb", formatted_data, updateData})
      doAutofill({part: "review_of_systems", formatted_data, updateData})
      setTab("opening_blurb");
      return;
    }
    let pend_set_tab;
    if (!has_autofilled.physical_exam) {
      if (formatted_data.allow_physical_exam) {
        doAutofill({part: "physical_exam", formatted_data, updateData})
        setTab("physical_exam");
        return;
      } else {
        updateHasAutofilled(draft => {draft["physical_exam"] = true});
        pend_set_tab = "diagnostics";
      }
    }
    if (!has_autofilled.diagnostics) {
      if (formatted_data.allow_diagnostics) {
        doAutofill({part: "diagnostics", formatted_data, updateData});
        if (!formatted_data.allow_interventions) {
          updateHasAutofilled(draft => {draft["interventions"] = true});
        }
        setTab("diagnostics");
        return;
      } else {
        updateHasAutofilled(draft => {draft["diagnostics"] = true});
        pend_set_tab = "interventions";
      }
    }
    if (!has_autofilled.interventions) {
      if (formatted_data.allow_interventions) {
        doAutofill({part: "interventions", formatted_data, updateData})
        setTab("interventions");
        return;
      } else {
        updateHasAutofilled(draft => {draft["interventions"] = true});
        pend_set_tab = "rubrics";
      }
    }
    if (pend_set_tab) {
      setTab(pend_set_tab);
      return;
    }
    // if (!has_autofilled.assessment_and_plan) {
    //   doAutofill({part: "assessment_and_plan", formatted_data, updateData})
    //   setTab("assessment_and_plan");
    //   return;
    // }

    const formatted_rubrics = rubrics.map(rubric => {
      return {
        data: rubric.data,
        ...(rubric._id ? {
          _id: rubric._id,
          version_id: rubric.version_id,
          is_linked: rubric.is_linked || false,
        } : {
          local_id: rubric.local_id,
        })
      }
    })

    const continueSubmit = () => {
      if (assessment_id_param && assessment_id_param != "new") {
        openUpdateFromLinkedAssessmentsChoiceModal({
          callback: ({ update_assessment_only }) => {
            submit({ data: formatted_data, rubrics: formatted_rubrics, update_assessment_only }, { method: "post", encType: "application/json" });
          }
        })
      } else {
        submit({ data: formatted_data, rubrics: formatted_rubrics }, { method: "post", encType: "application/json" });
      }
    }

    if (scenario_id === "new" && formatted_rubrics.length === 0 && !seenMissingRubricWarning) {
      openNoRubricScenarioWarningModal({ callback: continueSubmit });
    } else {
      continueSubmit();
    }
    
  }

  function handleValidateFailed({ element }) {
    setTab(element.closest("[tabname]").getAttribute("tabname"));
  }

  function doAutofill({part, formatted_data, updateData}) {
    updateHasAutofilled(draft => {draft[part] = true});
    updateTabsPendingAutofill(draft => {draft[part] = "loading"});

    autofillScenario({ part: part, scenario_data: formatted_data }).then(function(autofill_data) {
      updateData((draft) => {
        Object.assign(draft, autofill_data.data.new_scenario_data)
      })

      updateTabsPendingAutofill(draft => {delete draft[part]});
      
    }).catch(function(error) {
      console.error(error);
      updateTabsPendingAutofill(draft => {draft[part] = "error"});
    })
  }

  const last_tab = getLastTab(["patient_history",["opening_blurb","review_of_systems","physical_exam","diagnostics","interventions"],"questions"], has_autofilled);

  const goBackCallback = useCallback(() => {
    setInRubricEditor(false);
  }, [setInRubricEditor]);

  const submitCallback = useCallback(({ rubric }) => {
    if (in_rubric_editor.index != null) {
      updateRubrics(draft => {
        const file = draft[in_rubric_editor.index].file;
        draft[in_rubric_editor.index] = {
          ...rubric,
          ...((rubric._id && file) ? { file } : {}),
          ...(rubric._id ? { is_linked: true } : {}),
        };
      });
      if (rubric._id) {
        updateInitialRubrics(draft => {
          const existing_draft = draft[draft.findIndex(r => r._id == rubric._id)];
          draft[draft.findIndex(r => r._id == rubric._id)] = {
            ...existing_draft,
            version_id: rubric.version_id,
            data: rubric.data,
            // Leave .is_linked unchanged.
          };
        });
      }
    } else {
      updateRubrics(draft => {
        draft.push({
          ...rubric,
          ...(rubric._id ? {
            is_linked: true,
            file: {
              _level: "owner"
            }
          } : {})
        });
      });
    }
    setInRubricEditor(false);
  }, [in_rubric_editor, updateRubrics, setInRubricEditor, updateInitialRubrics]);

  return (
    <>
      <FormProvider onSubmit={submitForm} onValidateFailed={handleValidateFailed} initial_data={initial_data} has_changed={!compareRubricArrays(initial_rubrics, rubrics)} files={files} unsaved_modal={actionData?.success ? null : "new_scenario"}>
        <NavigateBackOnSuccess actionData={actionData} navigate={navigate} />
        <Form id="create_scenario" method="post" ref={form} onSubmit={(event) => event.preventDefault()}>
          <div className="dashboard_container dashboard_full_height">
            {in_rubric_editor ?
            <>
              <RubricEditor goBackCallback={goBackCallback} from_scenario_editor={true} rubric={in_rubric_editor.rubric} submitCallback={submitCallback}/>
            </> :
            <>
              <header className="dashboard_topbar">
                <div className="topbar_left">
                  <CircularMenuButton onClick={() => navigate(-1)} icon="arrow-left"/>
                  <div className="topbar_title">{initial_data ? "Edit" : "New"} scenario</div>
                </div>
                {/* <div className="topbar_right">
                  <div className="topbar_button">
                    {scenario_id !== "new" ? <Button icon="play-outline" className="hollow" text={is_mobile ? "Preview" : "Preview scenario"} onClick={() => navigate((course_id && assessment_id) ? "/courses/"+course_id+"/assessments/"+assessment_id+"/preview" : "/scenarios/"+scenario_id+"/preview")}/> : null}
                  </div>
                </div> */}
              </header>
              <div className="dashboard_main">
                <SidebarHeaderProvider>
                  <Sidebar>
                    <SidebarButton onClick={() => {setTab("basic_details")}} name="Overview" selected={tab == "basic_details"}/>
                    <SidebarButton onClick={() => {setTab("objectives")}} name="Objectives and features" selected={tab == "objectives"}/>
                    <SidebarButton onClick={() => {setTab("chief_concern")}} name="Reason for visit" selected={tab == "chief_concern"}/>
                    {/* <SidebarButton onClick={() => {setTab("prior_workup")}} name="Prior workup" selected={tab == "prior_workup"}/> */}
                    <SidebarButton onClick={() => {setTab("patient_details")}} name="Patient details" selected={tab == "patient_details"}/>
                    <SidebarButton onClick={() => {setTab("patient_behavior")}} name="Behavior" selected={tab == "patient_behavior"}/>
                    <SidebarButton onClick={() => {setTab("diagnoses")}} name="Diagnoses" selected={tab == "diagnoses"}/>
                    <SidebarButton onClick={() => {setTab("patient_history")}} name="History" selected={tab == "patient_history"}/>
                    <SidebarSpacing/>
                    <SidebarButton onClick={() => {setTab("opening_blurb")}} name="Opening line" selected={tab == "opening_blurb"} disabled={!has_autofilled.opening_blurb} shimmer={tabs_pending_autofill["opening_blurb"] == "loading" ? 1 : false}/>
                    <SidebarButton onClick={() => {setTab("review_of_systems")}} name="Review of Systems" selected={tab == "review_of_systems"} disabled={!has_autofilled.review_of_systems} shimmer={tabs_pending_autofill["review_of_systems"] == "loading" ? 2 : false}/>
                    <SidebarButton onClick={() => {setTab("physical_exam")}} name="Physical exam" selected={tab == "physical_exam"} disabled={!has_autofilled.physical_exam} shimmer={tabs_pending_autofill["physical_exam"] == "loading" ? 3 : false}/>
                    <SidebarButton onClick={() => {setTab("diagnostics")}} name="Diagnostics" selected={tab == "diagnostics"} disabled={!has_autofilled.diagnostics || !has_autofilled.physical_exam} shimmer={tabs_pending_autofill["diagnostics"] == "loading" ? 4 : false}/>
                    {interventions_enabled && <SidebarButton onClick={() => {setTab("interventions")}} name="Interventions" selected={tab == "interventions"} disabled={!has_autofilled.interventions || !has_autofilled.diagnostics || !has_autofilled.physical_exam} shimmer={tabs_pending_autofill["interventions"] == "loading" ? 5 : false}/>}
                    <SidebarSpacing/>
                    {/* <SidebarButton onClick={() => {setTab("assessment_and_plan")}} name="Case summary" selected={tab == "assessment_and_plan"} disabled={!has_autofilled.assessment_and_plan} shimmer={tabs_pending_autofill["assessment_and_plan"] == "loading" ? 5 : false}/> */}
                    <SidebarButton onClick={() => {setTab("rubrics")}} name="Rubrics" selected={tab == "rubrics"} disabled={!has_autofilled.interventions || !has_autofilled.diagnostics || !has_autofilled.physical_exam}/>
                    <SidebarButton onClick={() => {setTab("questions")}} name="Follow-up questions" selected={tab == "questions"} disabled={!has_autofilled.interventions || !has_autofilled.diagnostics || !has_autofilled.physical_exam}/>
                  </Sidebar>
                  <Scrollable className="dashboard_content" depencencies={[tab, tabs_pending_autofill, in_rubric_editor]}>
                    <BasicDetails visible={tab == "basic_details"}/>
                    <Objectives visible={tab == "objectives"} interventions_enabled={interventions_enabled}/>
                    <Diagnoses visible={tab == "diagnoses"}/>
                    <ChiefConcern visible={tab == "chief_concern"}/>
                    {/* <PriorWorkup visible={tab == "prior_workup"}/> */}
                    <Patient visible={tab == "patient_details"} editing_scenario={initial_data ? true : false}/>
                    <PatientBehavior visible={tab == "patient_behavior"} patient_custom_prompt_enabled={initial_data?.patient_custom_prompt ? true : false}/>
                    <PatientHistory visible={tab == "patient_history"}/>
                    <OpeningBlurb visible={tab == "opening_blurb" && !tabs_pending_autofill[tab]} has_autofilled={has_autofilled}/>
                    <PatientROS visible={tab == "review_of_systems" && !tabs_pending_autofill[tab]} has_autofilled={has_autofilled}/>
                    <PatientPhysicalExam visible={tab == "physical_exam" && !tabs_pending_autofill[tab]} has_autofilled={has_autofilled} enable_jit_fill={!tabs_pending_autofill.physical_exam && has_autofilled.physical_exam && !initial_data?.allow_physical_exam}/>
                    <PatientData visible={tab == "diagnostics" && !tabs_pending_autofill[tab]} has_autofilled={has_autofilled} enable_jit_fill={!tabs_pending_autofill.diagnostics && has_autofilled.diagnostics && !initial_data?.allow_diagnostics}/>
                    <PatientInterventions visible={tab == "interventions" && !tabs_pending_autofill[tab]} has_autofilled={has_autofilled} enable_jit_fill={!tabs_pending_autofill.interventions && has_autofilled.interventions && !initial_data?.allow_interventions}/>
                    {/* <AssessmentAndPlan visible={tab == "assessment_and_plan" && !tabs_pending_autofill[tab]} has_autofilled={has_autofilled}/> */}
                    <Rubrics visible={tab == "rubrics"} editing_scenario={initial_data ? true : false} setInRubricEditor={setInRubricEditor} rubrics={rubrics} updateRubrics={updateRubrics}/>
                    <Questions visible={tab == "questions"}/>
                    <AutofillPending state={tabs_pending_autofill[tab]} tab={tab}/>
                    <Pagination show_continue={!initial_data && tab != last_tab} getContainerElement={() => document.querySelector("#create_scenario")}>
                      <FormSubmitProvider>
                        {({handleSubmit, has_changed}) => {
                          if (last_tab == "questions" || initial_data) {
                            return <Button className="primary" text={initial_data ? "Save changes" : "Create scenario"} onClick={handleSubmit} disabled={Object.values(tabs_pending_autofill).some(status => status === "loading") || submitting || !has_changed || actionData?.success}/>
                          } else {
                            return <Button text="Continue" className="primary" icon="chevron-right" icon_right={true} onClick={handleSubmit} disabled={tabs_pending_autofill[last_tab]}/>
                          }
                        }}
                      </FormSubmitProvider>
                    </Pagination>
                  </Scrollable>
                </SidebarHeaderProvider>
              </div>
            </>}
          </div>
        </Form>
      </FormProvider>
    </>
  )
}

export function NavigateBackOnSuccess({ actionData, navigate }) {
  const lastSuccessDate = useRef(null);

  useEffect(() => {
    if (actionData?.success === true && actionData.date !== lastSuccessDate.current) {
      lastSuccessDate.current = actionData.date;
      navigate(-1);
    }
  }, [actionData, navigate]);

  return null;
}

function AutofillPending({state, tab}) {
  let tab_names = {
    opening_blurb: "Opening line",
    review_of_systems: "Review of Systems",
    physical_exam: "Physical exam",
    diagnostics: "Diagnostics",
    interventions: "Interventions",
    assessment_and_plan: "Case presentation summary"
  }

  return (
    <div style={{display: (state ? "contents" : "none")}}>
      <Header title={tab_names[tab]}/>
      <div className="dashboard_content_inner nearest_scrollable_container">
        {state == "loading" ? (
          <div className="error_box">
            <div>
              <div className="sparkles_animation"><Lottie animationData={sparklesAnimation} /></div>
              <div className="text_ai_shimmer">Generating {tab_names[tab].toLowerCase()}...</div>
            </div>
          </div>
        ) : (
          <div className="error_box">
            <div>
              <div><Icon name="alert-circle-outline"/></div>
              <div>Something went wrong</div>
              <div>
                <FormSubmitProvider>
                  {({handleSubmit}) => <Button text="Try again" onClick={() => {handleSubmit({validate: false, passthrough_args: {part: tab}})}}/>}
                </FormSubmitProvider>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function RegenerateAutofillButton({part}) {
  const { handleSubmit } = useFormSubmit();

  const [openConfirmModal] = useModal(({ context, closeModal, setLoading }) => {
    function submitRegenerate() {
      closeModal();
      handleSubmit({validate: false, passthrough_args: {part}});
    }

    return (
      <Modal title="Regenerate this section?">
        <ModalContent>
          Everything in this section will be replaced.
        </ModalContent>
        <ModalButtons>
          <Button text="Cancel" className="hollow" onClick={closeModal}/>
          <Button text="Confirm" className="primary" onClick={submitRegenerate}/>
        </ModalButtons>
      </Modal>
    )
  });

  return (
    <Button icon="refresh" text="Regenerate" className="" onClick={openConfirmModal}/>
  )
}

function AutoRegenerate({ part, empty_field_name, is_filled_fn, checkbox_field_name, enabled = false }) {
  const { handleSubmit } = useFormSubmit();
  const { data } = useFormData([empty_field_name, checkbox_field_name]);
  const [triggered, setTriggered] = useState(false);

  const empty_field_value = data[empty_field_name];
  const checkbox_field_value = data[checkbox_field_name];

  useEffect(() => {
    if (checkbox_field_value && !is_filled_fn(empty_field_value) && !triggered && enabled) {
      handleSubmit({validate: false, passthrough_args: {part}});
      setTriggered(true);
    }
  }, [empty_field_value, checkbox_field_value, triggered, is_filled_fn, part, handleSubmit, enabled]);

  return null;
}

function BasicDetails({visible}) {
  return (
    <div style={{display: (visible ? "contents" : "none")}} tabname="basic_details">
      <Header title="Overview"/>
      <div className="dashboard_content_inner nearest_scrollable_container" hidden={!visible}>
        <div className="dashboard_content_container">
          <Textbox name="title" title="Scenario title" placeholder="Appendicitis case" hint="Learners won't see this title" autofocus={visible} required="Please enter a scenario title"/>
          <Spacing/>
          <RadioGroup title="Conversation mode" name="conversation_mode" default_value="text">
            <RadioOption name="text" text="Text"/>
            <RadioOption name="voice" text="Voice"/>
          </RadioGroup>
          <FormDataProvider fields={["conversation_mode"]}>{({data}) => (
            <FormDisabled obviated={data.conversation_mode != "voice"} indented={true}>
              <Checkbox text="Allow switching to text mode if preferred" name="allow_text_mode" default_value={false}/>
              <Checkbox text="Show the transcript while talking" name="show_transcript" default_value={true}/>
            </FormDisabled>
          )}</FormDataProvider>
          <Spacing/>
          <RadioGroup title="Setting" name="setting" default_value="outpatient">
            <RadioOption name="outpatient" text="Outpatient clinic"/>
            <RadioOption name="inpatient" text="Inpatient"/>
            <RadioOption name="er" text="Emergency room"/>
          </RadioGroup>
          <Spacing/>
          <Textbox name="learner_role" title="What role does the learner play?" default_value="Clinician" required="Please describe what role the learner plays"/>
        </div>
      </div>
    </div>
  )
}

function Objectives({visible, interventions_enabled}) {
  return (
    <div style={{display: (visible ? "contents" : "none")}} tabname="objectives">
      <Header title="Objectives and features"/>
      <div className="dashboard_content_inner nearest_scrollable_container" hidden={!visible}>
        <div className="dashboard_content_container">
          <Checkbox text="Diagnose the patient" name="diagnose" default_value={true}/>
          <FormDataProvider fields={["diagnose"]}>{({data}) => (
            <FormDisabled obviated={!data.diagnose} indented={true}>
              <Checkbox text="Ask the learner to update a list of differentials throughout the encounter, to capture their reasoning process" name="capture_differentials" default_value={true}/>
            </FormDisabled>
          )}</FormDataProvider>
          <FormDisabled indented={true}>
            <Checkbox text="Allow the learner to consult with relevant medical experts for advice, to capture their gaps in knowledge" name="consult_for_advice" default_value={true}/>
          </FormDisabled>
          <HalfSpacing/>
          <Checkbox text="Have a focused conversation" name="conversation_based_objective" default_value={false}/>
          <FormDataProvider fields={["conversation_based_objective"]}>{({data}) => (
            <FormDisabled obviated={!data.conversation_based_objective} indented={true}>
              <HalfSpacing/>
              <Textbox name="conversation_based_objective_text" title="What's the goal of the conversation?" placeholder="Delivering bad news" required="Please enter a conversation-based objective" autofocus={data.conversation_based_objective}/>
            </FormDisabled>
          )}</FormDataProvider>
          <ThreeQuarterSpacing/>
          <Checkbox text="Physical exam" name="allow_physical_exam" default_value={true}/>
          <Checkbox text="Diagnostics" name="allow_diagnostics" default_value={true}/>
          <Checkbox text={interventions_enabled ? "Interventions" : "Interventions (coming soon)"} name="allow_interventions" default_value={false} disabled={!interventions_enabled}/>
          {/* <HalfSpacing/> */}
          <Checkbox text="Assessment and plan" name="assessment_and_plan" default_value={true}/>
        </div>
      </div>
    </div>
  )
}

function Diagnoses({visible}) {
  return (
    <div style={{display: (visible ? "contents" : "none")}} tabname="diagnoses">
      <Header title="Diagnoses"/>
      <div className="dashboard_content_inner nearest_scrollable_container" hidden={!visible}>
        <div className="dashboard_content_container">
          <div className="instructions_box">
            {/* <div>
              <div><Icon name="information-slab-circle-outline"/></div>
              <div>The learner won't know the diagnoses.</div>
            </div> */}
            <div>
              <div><Icon name="sparkles"/></div>
              <div>We'll use this to automatically generate a Review of Systems, physical exam findings, and diagnostic results for the patient.</div>
            </div>
          </div>
          <InlineHeader title="New diagnoses" subtitle="These are unknown to the learner and patient"/>
          <InlineSection>
            <FormDataProvider fields={["diagnose"]}>{({data}) => (
              <FormArrayGroup name="diagnoses" start_with_one={true} autofocus_first_entry={true} add_button_label="Add diagnosis" required={data.diagnose ? "Please add at least one diagnosis" : false}>
                {({ index, removeItem, just_added, obviated, disabled, readOnly }) => (
                  <div className="diagnosis_item">
                    <div>
                      <div>
                        <FormDataProvider fields={["differential_diagnoses"]}>{({data}) => (
                          <Textbox name="diagnosis" title="Diagnosis" placeholder={index == 0 ? "Appendicitis" : ""} required={(index == 0 || (data.differential_diagnoses || []).filter(a => a.trim().length > 0).length > 0) ? "Please enter a diagnosis" : false} autofocus={just_added && visible}/>
                        )}</FormDataProvider>
                        {index > 0 ? <div className="textbox_remove_item" onClick={removeItem} tabIndex={0} inert={(obviated || disabled) ? "true" : null}><Icon name="close"></Icon></div> : null}
                      </div>
                    </div>
                    <div>
                      <TextboxList name="differential_diagnoses" title="Differentials" add_button_label="Add differential" start_with_one={true}/>
                    </div>
                  </div>
                )}
              </FormArrayGroup>
            )}</FormDataProvider>
            <ThreeQuarterSpacing/>
            <HalfSpacing/>
          </InlineSection>
          <InlineHeader title="Established diagnoses" subtitle="The patient already knows these"/>
          <InlineSection>
            <TextboxList title="Diagnosis" name="established_diagnoses" add_button_label="Add diagnosis" height="short"/>
          </InlineSection>
        </div>
      </div>
    </div>
  )
}

function ChiefConcern({visible}) {
  return (
    <div style={{display: (visible ? "contents" : "none")}} tabname="chief_concern">
      <Header title="Reason for visit"/>
      <div className="dashboard_content_inner nearest_scrollable_container" hidden={!visible}>
        <div className="dashboard_content_container">
          {/* <div className="instructions_box">
            <div>
              <div><Icon name="information-slab-circle-outline"/></div>
              <div>Include only what the patient should know about themselves.</div>
            </div>
            <div>
              <div><Icon name="chat"/></div>
              <div>The patient actor will rephrase this in their own words.</div>
            </div> */}
            {/* <div>
              <div><Icon name="sparkles"/></div>
              <div>The patient actor will make up any specific details that aren't specified, which may differ between learners.</div>
            </div> */}
          {/* </div> */}
          <div className="halves">
            <div>
              <RadioGroup title="Reason for visit" name="reason_for_visit" required="Please choose">
                <RadioOption name="preventative" text="Preventative"/>
                <FormDataProvider fields={["reason_for_visit"]}>{({data}) => (
                  <FormDisabled indented={true} obviated={data.reason_for_visit != "preventative"}>
                    <div style={{display: (data.reason_for_visit == "preventative" ? "contents" : "none")}}>
                      <Dropdown name="reason_for_visit_preventative" other={true} default_value="Annual exam" required="Please choose">
                        <DropdownOption text="Annual exam"/>
                        <DropdownOption text="Well-child exam"/>
                        <DropdownOption text="Prenatal"/>
                      </Dropdown>
                      <HalfSpacing/>
                    </div>
                  </FormDisabled>
                )}</FormDataProvider>
                <RadioOption name="acute_care" text="Acute care"/>
                <RadioOption name="follow_up" text="Follow-up or chronic condition"/>
                <RadioOption name="procedure" text="Procedure or treatment"/>
              </RadioGroup>
            </div>
            <div>
              <RadioGroup title="Type" name="relationship_to_clinic" default_value="established" required="Please choose">
                <RadioOption name="established" text="Established patient"/>
                <RadioOption name="new" text="New patient"/>
              </RadioGroup>
            </div>
            <div></div>
          </div>
          <Spacing/>
          <div className="halves">
            <div>
              <Textbox name="chief_concern" title="Chief concern" multiline={true} placeholder="My foot hurts." required="Please enter the chief concern"/>
              <Spacing/>
              <Textbox name="history_of_present_illness" title="History of present illness" multiline={true} height={3} placeholder="Patient presents with..." required="Please enter the history of present illness" hint={"Make sure to include: Onset, location, duration, characteristic, alleviating and aggrevating factors, radiating or relieving factors, timing, and severity."}/>
            </div>
            <div>
              <Textbox name="workup_to_date_summary" title="Prior visits and workup done to date" multiline={true} height={5} hint={"Include:\n • Prior visits and referral history\n • Original reason for visit\n • Previous diagnostic results and treatments"}/>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// function PriorWorkup({ visible }) {
//   return (
//     <div style={{display: (visible ? "contents" : "none")}} tabname="prior_workup">
//       <Header title="Prior workup"/>
//       <div className="dashboard_content_inner nearest_scrollable_container" hidden={!visible}>
//         <div className="dashboard_content_container">
//           <div className="instructions_box">
//             <div>
//               <div><Icon name="information-slab-circle-outline"/></div>
//               <div>If applicable, include any details that you want the learner to have from the beginning.</div>
//             </div>
//             <div>
//               <div><Icon name="text-long"/></div>
//               <div>This will be shown verbatim, exactly as the text appears here.</div>
//             </div>
//           </div>
//           <Textbox name="workup_to_date_summary" title="Prior visits and workup done to date" multiline={true} height={5} hint={"Include:\n • Prior visits and referral history\n • Original reason for visit\n • Previous diagnostic results and treatments"}/>
//           {/* <Spacing/>
//           <Checkbox text="The patient was referred" name="referred" default_value={false}/>
//           <Spacing/>
//           <FormDataProvider>{({data}) => (
//             <FormDisabled obviated={!data.referred}>
//               <Textbox name="referred_from" title="Referred from" placeholder="Oncology" autofocus={visible}/>
//             </FormDisabled>
//           )}</FormDataProvider> */}
//         </div>
//       </div>
//     </div>
//   )
// }

function Patient({visible, editing_scenario}) {
  const [has_handled_sex_change, setHasHandledSexChange] = useState(false);
  const [has_handled_gender_change, setHasHandledGenderChange] = useState(false);
  const [random_names, setRandomNames] = useState(null);
  const [has_done_initial_name_load, setHasDoneInitialNameLoad] = useState(false);
  const { data, updateData } = useFormData(["patient_sex","patient_gender","patient_age","patient_race","speaker_age","speaker_gender","speaker_relationship_to_patient","whos_speaking"]);
  const [default_race] = useState(editing_scenario ? "" : randomizeRace({return_default: true}));
  const [used_races, setUsedRaces] = useState([default_race.race]);
  const [used_race_categories, setUsedRaceCategories] = useState([default_race.category]);
  const [voicePreviewLoading, setVoicePreviewLoading] = useState(false);
  const voicePreviewHowl = useRef(null);

  const age_years = convertAgeToYears(data.patient_age);
  const available_ages = Object.keys(random_names || []);
  const closest_age = available_ages.reduce((a, b) => Math.abs(b - (age_years ?? 30)) < Math.abs(a - (age_years ?? 30)) ? b : a, -9999);

  function handlePatientSexChange(sex) {
    if (!has_handled_sex_change && !editing_scenario) {
      setHasHandledSexChange(true);
      if (sex == "female") {
        updateData((draft) => {
          draft["patient_gender"] = "Female"
        });
        handlePatientGenderChange("Female", true);
      } else if (sex == "male") {
        updateData((draft) => {
          draft["patient_gender"] = "Male"
        });
        handlePatientGenderChange("Male", true);
      }

      if (age_years && age_years <= 10) {
        const speaker_age = Math.round(age_years) + 25 + Math.floor(Math.random() * 10);
        const speaker_gender = ["Female", "Male"][Math.floor(Math.random() * 2)];
        updateData((draft) => {
          draft["speaker_age"] = speaker_age;
          draft["speaker_gender"] = speaker_gender;
          draft["whos_speaking"] = "someone_else";
          draft["speaker_relationship_to_patient"] = {
            "Female": "Patient's mother",
            "Male": "Patient's father"
          }[speaker_gender];
        });
        randomizeVoice({age: speaker_age, gender: speaker_gender, speaker: true});
      }
    }
  }

  function handlePatientGenderChange(gender, automatic) {
    if (!has_handled_gender_change && !editing_scenario) {
      if (!automatic) {
        setHasHandledGenderChange(true);
      }
      if (gender == "Female") {
        updateData((draft) => {
          draft["patient_pronouns"] = "She/Her"
        });
      } else if (gender == "Male") {
        updateData((draft) => {
          draft["patient_pronouns"] = "He/Him"
        });
      } else if (gender == "Non-binary") {
        updateData((draft) => {
          draft["patient_pronouns"] = "They/Them"
        });
      }
      generateNewName({gender});
      randomizeMeasurements({gender});
      randomizeVoice({gender});
    }
  }

  function handleSpeakerGenderChange(gender) {
    randomizeVoice({gender, speaker: true});
  }

  function loadMoreNames() {
    generateRandomNames().then(function(names) {
      setRandomNames(names.data.names)
    }).catch(function(error) {
      console.error(error);
    });
  }

  useEffect(() => {
    if (!has_done_initial_name_load) {
      if (editing_scenario ? visible : true) {
        loadMoreNames();
        setHasDoneInitialNameLoad(true);
      }
    }
  }, [has_done_initial_name_load, editing_scenario, visible]);

  function raceToCodeForNames(race) {
    const race_keywords = {
      "White": "White",
      "Black": "Black",
      "African American": "Black",
      "Hispanic or Latino": "Hispanic",
      "White and Hispanic": "Hispanic",
      "White and Latino(a)": "Hispanic",
      "White with": "Hispanic",
      "Asian": "Asian/PI",
      "Pacific Islander": "Asian/PI"
    }
    const matching_race_keywords = Object.entries(race_keywords).map(([key, value]) => race?.includes(key) ? value : null).filter(a => a != null);
    const selected_race_keyword = matching_race_keywords.length > 0 ? shuffle(matching_race_keywords)[0] : "None";
    return selected_race_keyword;
  }

  function raceToCodeForVoices(race) {
    const race_keywords = {
      "White": "White",
      "Black": "Black or African American",
      "African American": "Black or African American",
      "Asian": "Asian",
      "Middle Eastern": "Middle Eastern or North African",
      "North African": "Middle Eastern or North African",
      "Native American": "Native American or Alaska Native",
      "Indian": "Native American or Alaska Native",
      "Maya": "Native American or Alaska Native",
      "Alaska Native": "Native American or Alaska Native",
      "Pacific Islander": "Native Hawaiian or Pacific Islander",
      "Native Hawaiian": "Native Hawaiian or Pacific Islander",
      "Hispanic or Latino": "Hispanic or Latino",
    }
    const matching_race_keywords = Object.entries(race_keywords).map(([key, value]) => race?.includes(key) ? value : null).filter(a => a != null);
    const selected_race_keyword = matching_race_keywords.length > 0 ? shuffle(matching_race_keywords)[0] : "White";
    return selected_race_keyword;
  }

  function genderToCode(gender) {
    const genders_to_codes = {
      "Female": "F",
      "Male": "M"
    }
    return genders_to_codes[gender] || "U";
  }

  const randomize_button_available = data.patient_gender && random_names?.[closest_age]?.[genderToCode(data.patient_gender)]?.[raceToCodeForNames(data.patient_race)]?.length > 0;

  function generateNewName({gender, race}) {
    const g = genderToCode(gender || data.patient_gender);
    const r = raceToCodeForNames(race || data.patient_race);

    const available_names = random_names?.[closest_age]?.[g]?.[r];

    if (available_names?.length > 0) {
      let name = available_names.shift();
      setRandomNames(random_names);
      updateData((draft) => {
        draft["patient_name"] = name
      });
      if (available_names.length == 5) {
        loadMoreNames();
      }
    }
  }

  function randomizeRace({return_default, also_randomize_name}) {
    let category = null;
    category = pickRandomEntry(Object.entries(races.config).map(([key, value]) => [key, value.weight]));

    if (!return_default) {
      while ((used_race_categories || []).includes(category)) {
        category = pickRandomEntry(Object.entries(races.config).map(([key, value]) => [key, value.weight]));
      }
    }
    
    let race = pickRandomEntry(Object.entries(races.categories[category]));
    let hispanic_suffix = "";
    if (races.config[category].hispanic_weight ? races.config[category].hispanic_weight > Math.random() : false) {
      hispanic_suffix = " "+pickRandomEntry(races.config[category].eligible_suffixes.map(suffix => [suffix, races.hispanic_suffixes[suffix]]));
    }

    if ((races.config[category].disallowed_combos || []).includes(race+hispanic_suffix)) {
      return randomizeRace({return_default, also_randomize_name});
    }

    let prefix = "";
    if (hispanic_suffix == "") {
      if (races.config[category].white_prefix_weight ? races.config[category].white_prefix_weight > Math.random() : false) {
        prefix = "White and ";
      } else if (races.config[category].black_prefix_weight ? races.config[category].black_prefix_weight > Math.random() : false) {
        prefix = "Black and ";
      }
    }

    let full_race_string = prefix+race+hispanic_suffix;

    if (return_default) {
      return {race: full_race_string, category: category};
    }

    if (used_races.includes(full_race_string)) {
      return randomizeRace({return_default, also_randomize_name});
    }

    setUsedRaces([full_race_string, ...used_races.slice(0, 80)]);
    setUsedRaceCategories([category, ...used_race_categories.slice(0, 3)]);
    updateData((draft) => {
      draft["patient_race"] = full_race_string
    });

    if (also_randomize_name) {
      generateNewName({race: full_race_string});
      randomizeVoice({race: full_race_string});
    }
  }

  function pickRandomEntry(entries) {
    let total = 0;
    for (const entry of entries) {
      total += entry[1];
    }

    let rand = Math.random() * total;
    let cumulative = 0;

    for (const entry of entries) {
      cumulative += entry[1];
      if (rand <= cumulative) {
        return entry[0];
      }
    }
    return entries[0][0];
  }

  function randomizeMeasurements({gender}) {
    const { height, weight, headCircumference } = generateMeasurements({ age: convertAgeToYears(data.patient_age) ?? 20, sex: (gender == "Male") ? "male" : ((gender == "Female") ? "female" : (data.patient_sex == "male" ? "male" : "female")) });

    updateData((draft) => {
      draft["patient_height"] = height;
      draft["patient_weight"] = weight;
      if (headCircumference) {
        draft["patient_head_circumference"] = headCircumference;
      }
    });
  }

  function getAgeBucketForVoices(age) {
    const age_buckets = [18, 25, 40, 60, 80];
    const age_category = age_buckets.reduce((a, b) => Math.abs(b - (age ?? 30)) < Math.abs(a - (age ?? 30)) ? b : a, -9999);
    if (age === 20 || age === 21) {
      return 25;
    }
    return age_category;
  }

  function randomizeVoice({gender, race, age, speaker = false}) {
    let selected_gender = gender || (speaker ? data.speaker_gender : data.patient_gender);
    let selected_age = age || (speaker ? data.speaker_age : age_years);
    let selected_race = race || data.patient_race;
    
    let gender_category = genderToCode(selected_gender);
    if (gender_category === "U") {
      gender_category = Math.random() > 0.5 ? "M" : "F";
    }

    const race_category = raceToCodeForVoices(selected_race);

    const age_category = getAgeBucketForVoices(selected_age);

    if (!selected_gender || !selected_age) {
      return;
    }

    const voice = voices.find((voice) => voice.gender === gender_category && voice.race_tags.includes(race_category) && voice.age === age_category)?.id || "gfRt6Z3Z8aTbpLfexQ7N";

    updateData((draft) => {
      if (speaker) {
        draft["speaker_voice"] = voice;
      } else {
        draft["patient_voice"] = voice;
      }
    });
  }

  function handleAgeChange({ number, unit }) {
    const old_bucket = getAgeBucketForVoices(age_years);
    const new_age = convertAgeToYears({ ...data.patient_age, ...(number != null ? { number } : {}), ...(unit != null ? { unit } : {}) }) || -1;
    const new_bucket = getAgeBucketForVoices(new_age);
    
    if (old_bucket !== new_bucket && new_age != -1) {
      randomizeVoice({age: new_age});
    }
  }

  function handleSpeakerAgeChange({ number }) {
    const old_bucket = getAgeBucketForVoices(data.speaker_age);
    const new_bucket = getAgeBucketForVoices(number);
    
    if (old_bucket !== new_bucket) {
      randomizeVoice({age: number, speaker: true});
    }
  }

  function playVoicePreview(voiceId) {
    if (!voiceId) return;

    // Stop and unload any currently playing preview
    if (voicePreviewHowl.current) {
      voicePreviewHowl.current.unload();
      voicePreviewHowl.current = null;
    }

    setVoicePreviewLoading(true);

    // Create new Howl instance
    const howl = new Howl({
      src: [`/sounds/voice_previews/${voiceId}.mp3`],
      preload: true,
      volume: 1.0,
      onload: () => {
        setVoicePreviewLoading(false);
        howl.play();
      },
      onloaderror: (id, error) => {
        console.error(`Failed to load voice preview: ${voiceId}`, error);
        setVoicePreviewLoading(false);
      },
      onend: () => {
        // Cleanup when audio finishes
        if (voicePreviewHowl.current === howl) {
          howl.unload();
          voicePreviewHowl.current = null;
        }
      }
    });

    voicePreviewHowl.current = howl;
  }

  function handleVoiceChange(voiceId) {
    // playVoicePreview(voiceId);
    // If I uncomment: Make this only play when you CHANGE the voice, not if you select the already selected voice
  }

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (voicePreviewHowl.current) {
        voicePreviewHowl.current.unload();
      }
    };
  }, []);

  const ran_relationship_autofill_count = useRef(0);
  const last_relationship_autofill_value = useRef(null);

  function handleSpeakerRelationshipToPatientBlur() {
    if (data.whos_speaking === "someone_else" && data.speaker_relationship_to_patient?.length > 0 && !data.speaker_relationship_to_patient.toLowerCase().includes("patient")) {
      if (ran_relationship_autofill_count.current > 8 || last_relationship_autofill_value.current === data.speaker_relationship_to_patient) {
        return;
      }
      ran_relationship_autofill_count.current++;
      last_relationship_autofill_value.current = data.speaker_relationship_to_patient;
      publicAction("autofillScenario", { part: "speaker_relationship_to_patient", scenario_data: { speaker_relationship_to_patient: data.speaker_relationship_to_patient } }).then(function(autofill_data) {
        const new_speaker_relationship_to_patient = autofill_data.data.new_scenario_data?.speaker_relationship_to_patient;
        if (new_speaker_relationship_to_patient) {
          updateData((draft) => {
            if (draft["speaker_relationship_to_patient"] === data.speaker_relationship_to_patient) {
              draft["speaker_relationship_to_patient"] = new_speaker_relationship_to_patient;
            }
          })
        }
      }).catch(function(error) {
        console.error(error);
      })
    }
  }

  return (
    <div style={{display: (visible ? "contents" : "none")}} tabname="patient_details">
      <Header title="Patient details" />
      <div className="dashboard_content_inner nearest_scrollable_container" hidden={!visible}>
        <div className="dashboard_content_container">
          <div className="halves">
            <div style={{flex: 1.4}}>
              <FormObjectGroup name="patient_age">
                <Textbox name="number" title="Age" placeholder={25} autofocus={visible} required="Please enter the patient's age" type="number" min={0} max={150} onChange={(number) => handleAgeChange({ number })}>
                  <Dropdown name="unit" default_value="years" required={true} onChange={(unit) => handleAgeChange({ unit })}>
                    <DropdownOption name="years" text="years"/>
                    <DropdownOption name="months" text="months"/>
                    <DropdownOption name="weeks" text="weeks"/>
                    <DropdownOption name="days" text="days"/>
                    <DropdownOption name="hours" text="hours"/>
                  </Dropdown>
                </Textbox>
              </FormObjectGroup>
              <Spacing/>
              <FormDataProvider fields={["patient_sex","patient_gender"]}>{({data}) => (
                <>
                  <div className="patient_gender_section">
                    <div>
                      <FormDisabled obviated={age_years != null ? false : true}>
                        <Dropdown name="patient_sex" title="Birth Sex" onChange={handlePatientSexChange} required="Please choose">
                          <DropdownOption name="female" text="Female"/>
                          <DropdownOption name="male" text="Male"/>
                          <DropdownOption name="intersex" text="Intersex"/>
                        </Dropdown>
                      </FormDisabled>
                    </div>
                    <div>
                      <FormDisabled obviated={!data.patient_sex}>
                        <Dropdown name="patient_gender" title="Gender" onChange={handlePatientGenderChange} other={true} required="Please choose">
                          <DropdownOption text="Female"/>
                          <DropdownOption text="Male"/>
                          <DropdownOption text="Non-binary"/>
                          <DropdownOption text="Genderqueer"/>
                          <DropdownOption text="Genderfluid"/>
                          <DropdownOption text="Agender"/>
                          <DropdownOption text="Two-Spirit"/>
                        </Dropdown>
                      </FormDisabled>
                    </div>
                    <div>
                      <FormDisabled obviated={!(data.patient_gender != null)}>
                        <Dropdown name="patient_pronouns" title="Pronouns" other={true} required="Please choose">
                          <DropdownOption text="She/Her"/>
                          <DropdownOption text="He/Him"/>
                          <DropdownOption text="They/Them"/>
                        </Dropdown>
                      </FormDisabled>
                    </div>
                  </div>
                  <Spacing/>
                  <FormDisabled obviated={!(data.patient_gender != null)}>
                    <div className="patient_name_section">
                      <div>
                        <Textbox name="patient_race" default_value={default_race.race} title="Race and Ethnicity" required="Please enter the patient's race and ethnicity"/>
                      </div>
                      <div>
                        <Button icon="refresh" text="Randomize" className="small" onClick={() => {randomizeRace({also_randomize_name: true})}} disabled={false}/>
                      </div>
                    </div>
                  </FormDisabled>
                </>
              )}</FormDataProvider>
              <Spacing/>
              <FormDataProvider fields={["patient_gender","patient_age","patient_voice","conversation_mode","whos_speaking"]}>
                {({data}) => {
                  let has_circumference = !(!data.patient_gender || (convertAgeToYears(data.patient_age) ?? 4) > 3);
                  
                  return <>
                    <FormDisabled obviated={!(data.patient_gender != null)}>
                      <div className="patient_name_section">
                        <div>
                          <Textbox name="patient_name" title="Name" required="Please enter the patient's preferred name"/>
                        </div>
                        <div>
                          <Button icon="refresh" text="Randomize" className="small" onClick={() => {generateNewName({})}} loading={!randomize_button_available && !(!(data.patient_gender != null))}/>
                        </div>
                      </div>
                    </FormDisabled>
                    <Spacing/>
                    <FormDisabled obviated={!(data.patient_gender != null) || data.whos_speaking === "someone_else"}>
                      {data.conversation_mode == "voice" ? <>
                        <div className="patient_name_section">
                          <div>
                            <Dropdown name="patient_voice" title="Voice" onChange={handleVoiceChange} required="Please choose a voice">
                              {voices.map((voice) => (
                                <DropdownOption key={voice.id} name={voice.id} text={voice.label}/>
                              ))}
                            </Dropdown>
                          </div>
                          <div>
                            <Button icon="play-outline" text="Preview" className="small hollow" onClick={() => playVoicePreview(data.patient_voice)} loading={voicePreviewLoading && data.whos_speaking !== "someone_else"} disabled={!data.patient_voice}/>
                          </div>
                        </div>
                        <Spacing/>
                      </> : null}
                    </FormDisabled>
                    <FormDisabled obviated={!(data.patient_gender != null)}>
                      <div className="patient_measurements_section">
                        <div>
                          <Textbox name="patient_height" title="Height" required="Please enter height"/>
                        </div>
                        <div>
                          <Textbox name="patient_weight" title="Weight" required="Please enter weight"/>
                        </div>
                        <div style={{display: has_circumference ? "block" : "none"}}>
                          <Textbox name="patient_head_circumference" title="Head circumference" required={has_circumference ? "Please enter" : false}/>
                        </div>
                      </div>
                    </FormDisabled>
                  </>
                }}
              </FormDataProvider>
              <Spacing/>
              <Textbox name="patient_disabilities" title="Disabilities" multiline={true} height="short" hint="Could be physical or intellectual."/>
              <Spacing/>
              <Textbox name="patient_other_details" title="Other personal details" multiline={true} hint="You might include beliefs, occupation, marital status, sexuality, etc."/>
            </div>
            <div style={{}}>
              <RadioGroup title="Who's speaking?" name="whos_speaking" default_value="patient" required={true}>
                <RadioOption name="patient" text="Patient themselves"/>
                <RadioOption name="someone_else" text="Someone else, on behalf of the patient:"/>
              </RadioGroup>
              <Spacing/>
              <FormDataProvider fields={["whos_speaking", "conversation_mode", "speaker_voice"]}>{({data}) => (
                <FormDisabled obviated={data.whos_speaking !== "someone_else"}>
                  <Textbox name="speaker_relationship_to_patient" title="Relationship to the patient" hint="For example: Patient's mother, Patient's spouse, etc." required={true} autofocus={data.whos_speaking === "someone_else"} onBlur={() => handleSpeakerRelationshipToPatientBlur()} />
                  <Spacing/>
                  {data.conversation_mode == "voice" && <>
                    <div className="patient_gender_section">
                      <div>
                        <div style={{width: 80}}>
                          <Textbox name="speaker_age" title="Age" placeholder={40} required={"Required."} type="number" min={0} max={150} onChange={(number) => handleSpeakerAgeChange({ number })} />
                        </div>
                      </div>
                      <div style={{flex: 2}}>
                        <Dropdown name="speaker_gender" title="Gender" onChange={handleSpeakerGenderChange} other={true} required="Please choose">
                          <DropdownOption text="Female"/>
                          <DropdownOption text="Male"/>
                          <DropdownOption text="Non-binary"/>
                        </Dropdown>
                      </div>
                    </div>
                    <Spacing/>
                    <div className="patient_name_section">
                      <div>
                        <Dropdown name="speaker_voice" title="Voice" required="Please choose a voice">
                          {voices.map((voice) => (
                            <DropdownOption key={voice.id} name={voice.id} text={voice.label}/>
                          ))}
                        </Dropdown>
                      </div>
                      <div>
                        <Button icon="play-outline" text="Preview" className="small hollow" onClick={() => playVoicePreview(data.speaker_voice)} loading={voicePreviewLoading && data.whos_speaking === "someone_else"} disabled={!data.speaker_voice}/>
                      </div>
                    </div>
                    <Spacing/>
                  </>}
                  <Textbox name="speaker_additional_details" title="Additional details" hint="Feel free to include any other relevant details about the speaker." multiline={true} />
                </FormDisabled>
              )}</FormDataProvider>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function PatientBehavior({visible, patient_custom_prompt_enabled}) {
  return (
    <div style={{display: (visible ? "contents" : "none")}} tabname="patient_behavior">
      <Header title="Behavior" />
      <div className="dashboard_content_inner nearest_scrollable_container" hidden={!visible}>
        <div className="dashboard_content_container">
          <Dropdown name="patient_affect" title="How to act" default_value="Calm and Cooperative" other={true} required={true}>
            <DropdownOption text="Calm and Cooperative"/>
            <DropdownOption text="Talkative or Hyperactive"/>
            <DropdownOption text="Anxious or Nervous"/>
            <DropdownOption text="Fearful or Distrustful"/>
            <DropdownOption text="Sad or Emotional"/>
            <DropdownOption text="Agitated or Irritable"/>
            <DropdownOption text="Resistant or Uncooperative"/>
            <DropdownOption text="Drowsy or Lethargic"/>
            <DropdownOption text="Confused or Disoriented"/>
            <DropdownOption text="Unresponsive or Withdrawn"/>
          </Dropdown>
          <Spacing/>
          <Textbox name="patient_special_instructions" title="Custom instructions" multiline={true} height={5} placeholder="You're very concerned about whether you'll still be able to play the piano."/>
          {patient_custom_prompt_enabled ? <>
            <Spacing/>
            <Textbox name="patient_custom_prompt" title="Custom prompt" multiline={true} fullWidth={true} height={15}/>
          </> : null}
        </div>
      </div>
    </div>
  )
}

function PatientHistory({visible}) {
  return (
    <div style={{display: (visible ? "contents" : "none")}} tabname="patient_history">
      <Header title="History"/>
      <div className="dashboard_content_inner nearest_scrollable_container" hidden={!visible}>
        <div className="dashboard_content_container">
          <div className="instructions_box">
            <div>
              <div><Icon name="chat"/></div>
              <div>The patient actor will rephrase this in their own words.</div>
            </div>
            <div>
              <div><Icon name="sparkles"/></div>
              <div>The patient actor will make up any personal details that aren't specified, which may differ between learners.</div>
            </div>
          </div>
          <Textbox name="past_medical_history" title="Past medical history" hint="Include current and pre-existing conditions." multiline={true} autofocus={visible}/>
          <Spacing/>
          <Textbox name="surgical_history" title="Surgical history" multiline={true}/>
          <Spacing/>
          <Textbox name="obstetric_history" title="Obstetric history" hint="Include gravidity, term, preterm, abortion, and living." multiline={true}/>
          <Spacing/>
          <Textbox name="family_medical_history" title="Family medical history" multiline={true}/>
          <Spacing/>
          <Textbox name="social_history" title="Social history and social determinants of health" hint="You might include smoking and drug use, sexual history, exercise history, economic stability, food security, neighborhood safety, etc." multiline={true}/>
          <Spacing/>
          <Textbox name="medications" title="Medications" hint="Include generic name, dose, route, and freqency." multiline={true}/>
          <Spacing/>
          <Textbox name="allergies" title="Allergies" hint="Include name and reaction." multiline={true}/>
          <Spacing/>
          <Textbox name="patient_other_pertinent_details" title="Other pertinent details" multiline={true}/>
        </div>
      </div>
    </div>
  )
}

function OpeningBlurb({visible, has_autofilled}) {
  return (
    <div style={{display: (visible ? "contents" : "none")}} tabname="opening_blurb">
      <Header title="Opening line">
        <RegenerateAutofillButton part="opening_blurb" />
      </Header>
      <div className="dashboard_content_inner nearest_scrollable_container" hidden={!visible}>
        <div className="dashboard_content_container">
          <div className="instructions_box">
            <div>
              <div><Icon name="information-slab-circle-outline"/></div>
              <div>Include any details that you want the learner to have from the beginning.</div>
            </div>
            <div>
              <div><Icon name="text-long"/></div>
              <div>This will be shown verbatim, exactly as the text appears here.</div>
            </div>
          </div>
          <Textbox name="opening_blurb" title="Opening line" multiline={true} autofocus={visible} required={has_autofilled.opening_blurb ? "Please enter an opening line" : false} />
        </div>
      </div>
    </div>
  )
}

function PatientROS({visible}) {
  return (
    <div style={{display: (visible ? "contents" : "none")}} tabname="review_of_systems">
      <Header title="Review of Systems">
        <RegenerateAutofillButton part="review_of_systems" />
      </Header>
      <div className="dashboard_content_inner nearest_scrollable_container" hidden={!visible}>
        <div className="dashboard_content_container">
          <div className="instructions_box">
            <div>
              <div><Icon name="information-slab-circle-outline"/></div>
              <div>This is how the patient will subjectively describe their own symptoms.</div>
            </div>
            <div>
              <div><Icon name="chat"/></div>
              <div>The patient actor will rephrase this in their own words.</div>
            </div>
            <div>
              <div><Icon name="sparkles"/></div>
              <div>The patient actor will make up more specific details about symptoms if asked, but they won't invent any new symptoms.</div>
            </div>
          </div>
          <Textbox name="ros_general" title="General/Constitutional" multiline={true} height="short"/>
          <Spacing/>
          <Textbox name="ros_head" title="Head" multiline={true} height="short"/>
          <Spacing/>
          <Textbox name="ros_eyes" title="Eyes" multiline={true} height="short"/>
          <Spacing/>
          <Textbox name="ros_ears" title="Ears" multiline={true} height="short"/>
          <Spacing/>
          <Textbox name="ros_nose" title="Nose" multiline={true} height="short"/>
          <Spacing/>
          <Textbox name="ros_throat" title="Throat" multiline={true} height="short"/>
          <Spacing/>
          <Textbox name="ros_neck" title="Neck" multiline={true} height="short"/>
          <Spacing/>
          <Textbox name="ros_respiratory" title="Respiratory" multiline={true} height="short"/>
          <Spacing/>
          <Textbox name="ros_cardiovascular" title="Cardiovascular" multiline={true} height="short"/>
          <Spacing/>
          <Textbox name="ros_gastrointestinal" title="Gastrointestinal" multiline={true} height="short"/>
          <Spacing/>
          <Textbox name="ros_genitourinary" title="Genitourinary" multiline={true} height="short"/>
          <Spacing/>
          <Textbox name="ros_musculoskeletal" title="Musculoskeletal" multiline={true} height="short"/>
          <Spacing/>
          <Textbox name="ros_dermatologic" title="Dermatologic" multiline={true} height="short"/>
          <Spacing/>
          <Textbox name="ros_neurologic" title="Neurologic" multiline={true} height="short"/>
          <Spacing/>
          <Textbox name="ros_psychiatric" title="Psychiatric" multiline={true} height="short"/>
          <Spacing/>
          <Textbox name="ros_endocrine" title="Endocrine" multiline={true} height="short"/>
          <Spacing/>
          <Textbox name="ros_hematologic" title="Hematologic" multiline={true} height="short"/>
          <Spacing/>
          <Textbox name="ros_other" title="Other" multiline={true} height="short"/>
        </div>
      </div>
    </div>
  )
}

function PatientPhysicalExam({visible, enable_jit_fill}) {
  return (
    <div style={{display: (visible ? "contents" : "none")}} tabname="physical_exam">
      <Header title="Physical exam">
        <RegenerateAutofillButton part="physical_exam" />
      </Header>
      <div className="dashboard_content_inner nearest_scrollable_container" hidden={!visible}>
        <div className="dashboard_content_container">
          <div className="instructions_box">
            {/* All routine inspection findings are given to the learner immediately, but physical test findings are only provided if asked for. */}
            <div>
              <div><Icon name="human-handsdown"/></div>
              <div>The learner first selects which areas to include in the routine physical exam. This is the left side of the page.</div>
            </div>
            <div>
              <div><Icon name="human-search"/></div>
              <div>After the routine exam, the learner can perform follow-up physical exam tests to investigate further. This is the right side of the page.</div>
            </div>
            <div>
              <div><Icon name="text-long"/></div>
              <div>Findings specified here will be reported verbatim and will be the same for all learners.</div>
            </div>
            <div>
              <div><Icon name="sparkles"/></div>
              <div>If allowed, any physical test findings not specified here will be automatically generated, and may differ slightly between learners.</div>
            </div>
            {/* to be consistent with the diagnoses and patient information */}
          </div>
          <Checkbox text="Allow the learner to perform a physical exam" name="allow_physical_exam" default_value={true}/>
          <AutoRegenerate part="physical_exam" empty_field_name="physical_exam" is_filled_fn={value => Object.values(value).some(v => v.routine?.length > 0 || v.tests?.length > 0)} checkbox_field_name="allow_physical_exam" enabled={enable_jit_fill}/>
          <FormDataProvider fields={["allow_physical_exam"]}>{({data}) => (
            <FormDisabled obviated={!data.allow_physical_exam}>
              {/* <Checkbox text="Allow any physical exam test, even if not listed here" name="allow_physical_exam_any" default_value={true}/> */}
              <Spacing/>
              <FormObjectGroup name="physical_exam">
                <PhysicalExamItem name="vitals" title="Vitals and measurements"/>
                <PhysicalExamItem name="general_appearance" title="General appearance"/>
                <PhysicalExamItem name="heent" title="Head, eyes, ears, nose, and throat"/>
                <PhysicalExamItem name="lungs" title="Lungs"/>
                <PhysicalExamItem name="heart" title="Heart"/>
                <PhysicalExamItem name="abdomen" title="Abdomen"/>
                <PhysicalExamItem name="extremities" title="Extremities"/>
                <PhysicalExamItem name="lymph_nodes" title="Lymph nodes"/>
                <PhysicalExamItem name="skin" title="Skin"/>
                <PhysicalExamItem name="neuro" title="Neuro"/>
                <PhysicalExamItem name="other" title="Other" is_other={true}/>
              </FormObjectGroup>
            </FormDisabled>
          )}</FormDataProvider>
        </div>
      </div>
    </div>
  )
}

function PhysicalExamItem({ name, title, is_other = false }) {
  const { handleSubmit: handleSubmitOuter } = useFormSubmit();

  const [openAddModal] = useModal(({ context, closeModal, setLoading }) => {
    function submitAddForm({ formatted_data, focusError, passthrough_args }) {
      setLoading(true);
      autofillScenarioAddItem({ part: "physical_exam", scenario_data: passthrough_args.formatted_data, item_name: formatted_data.name }).then((data) => {

        closeModal();

        let results = data.data.response_data.results;
  
        if (results.length > 0) {
          context.callbackfn(results.map(result => { return {name: result.name, finding: result.result}}));
        } else {
          context.callbackfn([{name: formatted_data.name, finding: ""}]);
        }
  
      }).catch((error) => {
        console.error(error);
        setLoading(false);
        focusError({name: "name", error: "Something went wrong, please try again."});
      })
    }

    return (
      <Modal title="Add physical exam test">
        <FormProvider onSubmit={submitAddForm}>
          <FormSubmitProvider>
            {({handleSubmit}) => (
              <>
                <ModalContent>
                  <Textbox name="name" placeholder="Leg raise" onEnter={() => handleSubmitOuter({validate: false, callback: ({formatted_data}) => {handleSubmit({passthrough_args: { formatted_data }})}})} required="Please enter the physical exam test name" />
                </ModalContent>
                <ModalButtons>
                  <Button text="Cancel" className="hollow" onClick={closeModal}/>
                  <Button text="Submit" className="primary" onClick={() => handleSubmitOuter({validate: false, callback: ({formatted_data}) => {handleSubmit({passthrough_args: { formatted_data }})}})}/>
                </ModalButtons>
              </>
            )}
          </FormSubmitProvider>
        </FormProvider>
      </Modal>
    )
  })

  function handleAddItem(callbackfn) {
    openAddModal({ callbackfn })
  }

  return (
    <FormObjectGroup name={name}>
      <div className="physical_exam_item">
        <div>{title}</div>
        <div>
          <div>
            <Textbox name="routine" title="Routine inspection" multiline={true} long={true} hint={is_other ? "This box is always given as part of the routine exam, regardless of which areas are selected." : undefined}/>
          </div>
          <div>
            <FormArrayGroup title="Physical tests" name="tests" add_button_label="Add test" handleAddItem={handleAddItem} WrapperComponent={WrapperComponent}>
              {({ index, removeItem, just_added, obviated, disabled, readOnly }) => (
                <div className="textbox_list_item textbox_list_item_key_value no_labels">
                  <div style={{display: "none"}}><Icon name="drag_indicator"></Icon></div>
                  <div>
                    <FormDataProvider fields={["name","finding","images"]}>{({data}) => (
                      <>
                        <Textbox name="name" placeholder="Name" required={data.finding?.trim()?.length > 0 ? "Both fields are required for every item" : false}/>
                        {data.images?.length > 0 ? <Spacing/> : <ThreeQuarterSpacing/>}
                        <FilePicker name="images" type="image" multiple={true} small={true}/>
                        {data.images?.length > 0 ? <Spacing/> : <ThreeQuarterSpacing/>}
                        <div style={{display: data.images?.length > 0 ? "contents" : "none"}}>
                          <Checkbox text={`Ask the learner to interpret the image${data.images?.length > 1 ? "s" : ""}`} name="learner_interpret" default_value={true}/>
                          <Checkbox text="Give them my interpretation below" name="correct_interpretation" default_value={true}/>
                          <ThreeQuarterSpacing/>
                        </div>
                        <Textbox name="finding" title={data.images?.length > 0 ? `Describe what the learner should interpret from the image${data.images?.length > 1 ? "s" : ""}` : undefined} placeholder="Finding" multiline={true} required={data.name?.trim()?.length > 0 ? "Both fields are required for every item" : false}/>
                      </>
                    )}</FormDataProvider>
                  </div>
                  {!readOnly ? <div className="textbox_remove_item" onClick={removeItem} tabIndex={0} inert={(obviated || disabled) ? "true" : null}><Icon name="close"></Icon></div> : null}
                </div>
              )}
            </FormArrayGroup>
          </div>
        </div>
      </div>
    </FormObjectGroup>
  )
}

const WrapperComponent = ({children}) => {
  return (
    <div style={{paddingBottom: "5px"}}>
      {children}
    </div>
  )
}

function PatientData({visible, enable_jit_fill}) {
  const { handleSubmit: handleSubmitOuter } = useFormSubmit();

  const [openAddModal] = useModal(({ context, closeModal, setLoading }) => {
    function submitAddForm({ formatted_data, focusError, passthrough_args }) {
      setLoading(true);
      autofillScenarioAddItem({ part: "diagnostics", scenario_data: passthrough_args.formatted_data, item_name: formatted_data.name }).then((data) => {

        closeModal();

        let results = data.data.response_data.results;
  
        if (results.length > 0) {
          context.callbackfn(results.map(result => { return {name: result.name, result: result.result}}));
        } else {
          context.callbackfn([{name: formatted_data.name, result: ""}]);
        }
  
      }).catch((error) => {
        console.error(error);
        setLoading(false);
        focusError({name: "name", error: "Something went wrong, please try again."});
      })
    }

    return (
      <Modal title="Add diagnostic test">
        <FormProvider onSubmit={submitAddForm}>
          <FormSubmitProvider>
            {({handleSubmit}) => (
              <>
                <ModalContent>
                  <Textbox name="name" placeholder="Complete Blood Count" onEnter={() => handleSubmitOuter({validate: false, callback: ({formatted_data}) => {handleSubmit({passthrough_args: { formatted_data }})}})} required="Please enter the diagnostic test name" />
                </ModalContent>
                <ModalButtons>
                  <Button text="Cancel" className="hollow" onClick={closeModal}/>
                  <Button text="Submit" className="primary" onClick={() => handleSubmitOuter({validate: false, callback: ({formatted_data}) => {handleSubmit({passthrough_args: { formatted_data }})}})}/>
                </ModalButtons>
              </>
            )}
          </FormSubmitProvider>
        </FormProvider>
      </Modal>
    )
  });

  function handleAddItem(callbackfn) {
    openAddModal({ callbackfn });
  }

  return (
    <div style={{display: (visible ? "contents" : "none")}} tabname="diagnostics">
      <Header title="Diagnostics">
        <RegenerateAutofillButton part="diagnostics" />
      </Header>
      <div className="dashboard_content_inner nearest_scrollable_container" hidden={!visible}>
        <div className="dashboard_content_container">
          <div className="instructions_box">
            <div>
              <div><Icon name="test-tube"/></div>
              <div>List the results of any diagnostics or imaging that you expect the learner to order.</div>
            </div>
            <div>
              <div><Icon name="text-long"/></div>
              <div>Results specified here will be reported verbatim and will be the same for all learners.</div>
            </div>
            <div>
              <div><Icon name="sparkles"/></div>
              <div>If allowed, any results not specified here will be automatically generated, and may differ between learners.</div>
            </div>
          </div>
          <Checkbox text="Allow the learner to order diagnostics" name="allow_diagnostics" default_value={true}/>
          <AutoRegenerate part="diagnostics" empty_field_name="diagnostics_results" is_filled_fn={value => value?.length > 0} checkbox_field_name="allow_diagnostics" enabled={enable_jit_fill}/>
          <FormDataProvider fields={["allow_diagnostics"]}>
            {({data}) => (
              <FormDisabled obviated={!data.allow_diagnostics}>
                {/* <Checkbox text="Allow any diagnostic test, even if not listed here" name="allow_diagnostics_any" default_value={true}/> */}
                <Spacing/>
                <FormArrayGroup title="Diagnostics and imaging results" name="diagnostics_results" add_button_label="Add test" handleAddItem={handleAddItem} WrapperComponent={WrapperComponent}>
                  {({ index, removeItem, just_added, obviated, disabled, readOnly }) => (
                    <div className="textbox_list_item textbox_list_item_key_value no_labels">
                      <div style={{display: "none"}}><Icon name="drag_indicator"></Icon></div>
                      <div>
                        <FormDataProvider fields={["name","result","images"]}>{({data}) => (
                          <>
                            <Textbox name="name" placeholder="Name" required={data.result?.trim()?.length > 0 ? "Both fields are required for every item" : false}/>
                            {data.images?.length > 0 ? <Spacing/> : <ThreeQuarterSpacing/>}
                            <FilePicker name="images" type="image" multiple={true} small={true}/>
                            {data.images?.length > 0 ? <Spacing/> : <ThreeQuarterSpacing/>}
                            <div style={{display: data.images?.length > 0 ? "contents" : "none"}}>
                              <Checkbox text={`Ask the learner to interpret the image${data.images?.length > 1 ? "s" : ""}`} name="learner_interpret" default_value={true}/>
                              <Checkbox text="Give them my interpretation below" name="correct_interpretation" default_value={true}/>
                              <ThreeQuarterSpacing/>
                            </div>
                            <Textbox name="result" title={data.images?.length > 0 ? `Describe what the learner should interpret from the image${data.images?.length > 1 ? "s" : ""}` : undefined} placeholder="Result" multiline={true} required={data.name?.trim()?.length > 0 ? "Both fields are required for every item" : false}/>
                          </>
                        )}</FormDataProvider>
                      </div>
                      {!readOnly ? <div className="textbox_remove_item" onClick={removeItem} tabIndex={0} inert={(obviated || disabled) ? "true" : null}><Icon name="close"></Icon></div> : null}
                    </div>
                  )}
                </FormArrayGroup>
              </FormDisabled>
            )}
          </FormDataProvider>
        </div>
      </div>
    </div>
  )
}

function PatientInterventions({visible, enable_jit_fill}) {
  const { handleSubmit: handleSubmitOuter } = useFormSubmit();

  const [openAddModal] = useModal(({ context, closeModal, setLoading }) => {
    function submitAddForm({ formatted_data, focusError, passthrough_args }) {
      setLoading(true);
      autofillScenarioAddItem({ part: "interventions", scenario_data: passthrough_args.formatted_data, item_name: formatted_data.name }).then((data) => {

        closeModal();

        let results = data.data.response_data.interventions;
  
        if (results.length > 0) {
          context.callbackfn(results.map(result => { return {name: result.name, impact: result.impact, result: result.result, effect: result.effect}}));
        } else {
          context.callbackfn([{name: formatted_data.name, impact: "", result: "", effect: ""}]);
        }
  
      }).catch((error) => {
        console.error(error);
        setLoading(false);
        focusError({name: "name", error: "Something went wrong, please try again."});
      })
    }

    return (
      <Modal title="Add intervention">
        <FormProvider onSubmit={submitAddForm}>
          <FormSubmitProvider>
            {({handleSubmit}) => (
              <>
                <ModalContent>
                  <Textbox name="name" placeholder="Administer IV Normal Saline" onEnter={() => handleSubmitOuter({validate: false, callback: ({formatted_data}) => {handleSubmit({passthrough_args: { formatted_data }})}})} required="Please enter the intervention name" />
                </ModalContent>
                <ModalButtons>
                  <Button text="Cancel" className="hollow" onClick={closeModal}/>
                  <Button text="Submit" className="primary" onClick={() => handleSubmitOuter({validate: false, callback: ({formatted_data}) => {handleSubmit({passthrough_args: { formatted_data }})}})}/>
                </ModalButtons>
              </>
            )}
          </FormSubmitProvider>
        </FormProvider>
      </Modal>
    )
  });

  function handleAddItem(callbackfn) {
    openAddModal({ callbackfn });
  }

  return (
    <div style={{display: (visible ? "contents" : "none")}} tabname="interventions">
      <Header title="Interventions">
        <RegenerateAutofillButton part="interventions" />
      </Header>
      <div className="dashboard_content_inner nearest_scrollable_container" hidden={!visible}>
        <div className="dashboard_content_container">
          <div className="instructions_box">
            <div>
              <div><Icon name="needle"/></div>
              <div>Interventions are direct actions performed on the patient during the encounter that alter the patient’s physiology in real time, such as administering medications, giving fluids, or performing procedures like intubation — not prescriptions, referrals, or other plans for future care (those belong in the Assessment and Plan).</div>
            </div>
            <div>
              <div><Icon name="text-long"/></div>
              <div>Outcomes specified here will usually be reported verbatim, but may be changed by the AI to account for the combined effects of prior interventions.</div>
            </div>
            <div>
              <div><Icon name="sparkles"/></div>
              <div>If allowed, any safe intervention not specified here will be automatically generated, and may differ slightly between learners.</div>
            </div>
            <div>
              <div><Icon name="alert-octagon-outline"/></div>
              <div>{`By default, harmful and catastrophic interventions aren't allowed to be performed. If attempted, it will be recorded in the transcript but the learner will be able to continue as if it never happened.`}</div>
            </div>
          </div>
          <Checkbox text="Allow the learner to perform interventions" name="allow_interventions" default_value={false}/>
          <AutoRegenerate part="interventions" empty_field_name="interventions" is_filled_fn={value => value?.length > 0} checkbox_field_name="allow_interventions" enabled={enable_jit_fill}/>
          <Checkbox text="Allow any safe intervention, even if not listed here" name="allow_interventions_any" default_value={false}/>
          <Spacing/>
          <FormDataProvider fields={["allow_interventions"]}>
            {({data}) => (
              <FormDisabled obviated={!data.allow_interventions}>
                <FormArrayGroup title="Interventions" name="interventions" add_button_label="Add intervention" handleAddItem={handleAddItem} WrapperComponent={WrapperComponent}>
                  {({ index, removeItem, just_added, obviated, disabled, readOnly }) => (
                    <div className="textbox_list_item textbox_list_item_key_value no_labels">
                      <div style={{display: "none"}}><Icon name="drag_indicator"></Icon></div>
                      <div>
                        <FormDataProvider fields={["impact", "harmful", "allow_harmful_intervention"]}>{({data}) => (
                          <>
                            <Textbox name="name" placeholder="Name" required={true}/>
                            <Spacing/>
                            <InterventionRating name="impact" required={true}/>
                            {data.impact === "catastrophic" ? <HalfSpacing/> : <Spacing/>}
                            {data.impact === "harmful" && <>
                              <Checkbox text="Allow this harmful intervention to be performed" name="allow_harmful_intervention" default_value={false}/>
                              <ThreeQuarterSpacing/>
                            </>}
                            <FormDisabled obviated={(data.impact === "harmful" && !data.allow_harmful_intervention) || data.impact === "catastrophic"}>
                              <div style={{display: data.impact === "catastrophic" ? "none" : "contents"}}>
                                <Textbox name="result" title={"Immediate observable outcome"} hint="This is reported to the learner verbatim." placeholder="" multiline={true} required={true}/>
                                <Spacing/>
                                <Textbox name="effect" title={"Underlying clinical changes"} hint="Describe changes to symptoms, physical findings, diagnostic tests, etc." placeholder="" multiline={true} required={true}/>
                              </div>
                            </FormDisabled>
                          </>
                        )}</FormDataProvider>
                      </div>
                      {!readOnly ? <div className="textbox_remove_item" onClick={removeItem} tabIndex={0} inert={(obviated || disabled) ? "true" : null}><Icon name="close"></Icon></div> : null}
                    </div>
                  )}
                </FormArrayGroup>
              </FormDisabled>
            )}
          </FormDataProvider>
        </div>
      </div>
    </div>
  )
}

function AssessmentAndPlan({visible, has_autofilled}) {
  return (
    <div style={{display: (visible ? "contents" : "none")}} tabname="assessment_and_plan">
      <Header title="Case presentation summary">
        <RegenerateAutofillButton part="assessment_and_plan" />
      </Header>
      <div className="dashboard_content_inner nearest_scrollable_container" hidden={!visible}>
        <div className="dashboard_content_container">
          <div className="instructions_box">
            <div>
              <div><Icon name="sparkles"/></div>
              <div>Our AI system will use this summary, together with your rubrics, to give learners feedback on their assessments.</div>
            </div>
            <div>
              <div><Icon name="pencil-outline"/></div>
              <div>Please review and edit this summary to double check our AI system's understanding of your case, and to tune the feedback given to learners.</div>
            </div>
          </div>
          <Textbox name="assessment_and_plan_rubric" title="Case presentation summary" markdown={true} fullWidth={true} autofocus={visible} required={has_autofilled.assessment_and_plan ? "Please enter the case presentation summary" : null}/>
          {/* <div className="instructions_box">
            <div>
              <div><Icon name="information-slab-circle-outline"/></div>
              <div>List the key parts of the correct treatment plan. This will be used for grading and feedback. Make sure to include any treatments, medications, recommendations, referrals/consults, and/or follow-up instructions.</div>
            </div>
          </div>
          <TextboxList title="Treatment plan" name="treatment_plan" add_button_label="Add item" start_with_one={true} multiline={true} height="short" reorderable={true} autofocus={visible}/> */}
        </div>
      </div>
    </div>
  )
}

export function Rubrics({ visible, editing_scenario, is_assessment = false, setInRubricEditor, rubrics, updateRubrics, is_diagnosis_case: is_diagnosis_case_prop, feedback_mode }) {

  const { data } = useFormData(["diagnose"]);

  const is_diagnosis_case = data.diagnose || is_diagnosis_case_prop || false;

  const suggested_rubrics_query = useQuery({
    queryKey: ['suggested_rubrics', is_diagnosis_case],
    queryFn: () => {
      return publicAction("getRubricSuggestions", { is_diagnosis_case }).then(({ data }) => {
        return data.value;
      })
    },
    enabled: editing_scenario ? visible : true,
    staleTime: 10 * 1000
  })

  const suggested_rubrics = (suggested_rubrics_query.data || []).filter((file) => !rubrics.some((r) => r._id === file.item_id));

  const handleAddSuggestedRubric = (rubric) => {
    let new_rubric = { ...rubric, is_linked: true };

    if (!rubrics.some((r) => r._id === new_rubric._id)) {
      updateRubrics([...rubrics, new_rubric]);
    } else {
      updateRubrics(draft => {
        let index = rubrics.findIndex((r) => r._id === new_rubric._id);
        draft[index] = { ...new_rubric };
        return draft;
      });
    }
  }

  const [confirmDeleteLocalRubric] = useModal(function ({ context, closeModal }) {
    const { callback, is_local_only, edited_for_assessment } = context;

    function submitDeleteLocalRubric() {
      closeModal();
      callback();
    }

    return (
      <Modal title="Delete rubric?">
        <ModalContent>
          {is_local_only ? (
            `This rubric isn't saved anywhere else, so it will be permanently deleted.`
          ) : (
            <>
              {`This rubric was edited just for this ${edited_for_assessment ? "assessment" : "scenario"}. This edited version will be permanently deleted.`}
              <Spacing/>
              {`The original rubric file won't be deleted.`}
            </>
          )}
        </ModalContent>
        <ModalButtons>
          <Button text="Cancel" className="hollow" onClick={closeModal}/>
          <Button text="Delete rubric" className="destructive" onClick={submitDeleteLocalRubric}/>
        </ModalButtons>
      </Modal>
    )
  })

  const handleRemoveRubric = (rubric) => {
    if (rubric._id || (is_assessment && rubric.from_scenario)) {
      doRemoveRubric();
    } else {
      confirmDeleteLocalRubric({ callback: doRemoveRubric, is_local_only: !rubric.data.title, edited_for_assessment: !(rubric.from_scenario || !is_assessment) });
    }
    
    function doRemoveRubric() {
      updateRubrics(rubrics.filter((r) => r !== rubric));
    }
  }

  const handleUnlinkRubric = (rubric_id) => {
    updateRubrics(draft => {
      let index = rubrics.findIndex((r) => r._id == rubric_id);
      if (index != -1) {
        draft[index].is_linked = false;
        draft[index].from_scenario = false;
      }
    });
  }

  const handleRelinkRubric = (rubric_id) => {
    function setLoading(loading) {
      updateRubrics(draft => {
        let index = rubrics.findIndex((r) => r._id == rubric_id);
        if (index != -1) {
          draft[index].is_relinking_loading = loading;
        }
      });
    }

    setLoading(true);

    publicAction("getRubricOverview", { id: rubric_id }).then(({ data }) => {
      if (data.value) {
        const { file, rubric } = data.value;

        updateRubrics(draft => {
          let index = rubrics.findIndex((r) => r._id == rubric_id);
          if (index != -1) {
            draft[index] = {
              data: rubric.data,
              version_id: rubric.version_id,
              _id: rubric._id,
              is_linked: true,
              file
            };
          }
        });
      
      } else {
        openErrorModal();
        setLoading(false);
      }
    }).catch((error) => {
      setLoading(false);
      console.error(error);
      openErrorModal();
    })
  }
  
  const [openErrorModal] = useErrorModal();
  const [openFilePickerModal] = useFilePickerModal();

  const handleBrowseRubrics = () => {
    openFilePickerModal({
      mode: "choose",
      type: "rubric",
      callback: ({ file, setLoading, closeModal }) => {
        setLoading(true);
        publicAction("getRubricOverview", { id: file.item_id }).then(({ data }) => {
          if (data.value) {
            const { file, rubric } = data.value;
            closeModal();
            handleAddSuggestedRubric({
              data: rubric.data,
              version_id: rubric.version_id,
              _id: rubric._id,
              file
            });
          } else {
            closeModal();
          }
        }).catch((error) => {
          closeModal();
          console.error(error);
          openErrorModal();
        })
      }
    })
  }

  const [openNoPermissionModal] = useModal(({ context, closeModal }) => {
    return (
      <Modal title={`Edit just for this ${context.is_assessment ? "assessment" : "scenario"}?`}>
        <ModalContent>
          {`You don't have permission to edit the original rubric file, so any changes you make will be saved for this ${context.is_assessment ? "assessment" : "scenario"} only.`}
        </ModalContent>
        <ModalButtons>
          <Button text="Cancel" className="hollow" onClick={closeModal}/>
          <Button text={`Edit just for this ${context.is_assessment ? "assessment" : "scenario"}`} onClick={() => {
            closeModal();
            context.callback();
          }}/>
        </ModalButtons>
      </Modal>
    )
  });

  const handleClickRubric = (rubric) => {
    const editCallback = () => {
      setInRubricEditor({ rubric, index: rubrics.findIndex((r) => r == rubric) });
    }
    if (!rubric._id || !rubric.is_linked || ["edit", "owner"].includes(rubric.file?._level) || rubric._id?.startsWith("default")) {
      editCallback();
    } else {
      openNoPermissionModal({ callback: editCallback, is_assessment: is_assessment });
    }
  }

  const handleRubricSaved = ({ rubric, new_rubric, id, version_id, file_id }) => {
    updateRubrics(draft => {
      let index = rubrics.findIndex((r) => r == rubric);
      let { local_id, ...rest } = new_rubric;
      draft[index] = {
        ...rest,
        _id: id,
        is_linked: true,
        version_id: version_id,
        file: {
          _level: "owner"
        }
      };
      return draft;
    });
  }

  return (
    <div style={{display: (visible ? "contents" : "none")}} tabname="rubrics">
      <Header title="Rubrics"/>
      <div className="dashboard_content_inner nearest_scrollable_container" hidden={!visible}>
        <div className="dashboard_content_container">
          <div className="instructions_box">
            <div>
              <div><Icon name="sparkles"/></div>
              <div>{`Our AI system will use these rubrics to score and give feedback to learners.`}</div>
            </div>
            {is_assessment && (
              feedback_mode === "auto-return" ? (
                <div>
                  <div><Icon name="clock-outline"/></div>
                  <div>{`Feedback will be provided to learners immediately, without your review. You can change this in the course settings.`}</div>
                </div>
              ) : (
                <div>
                  <div><Icon name="pencil-outline"/></div>
                  <div>{`You'll get to review and edit feedback before it's sent to learners.`}</div>
                </div>
              )
            )}
            {!is_assessment && <div>
              <div><Icon name="book-open-plus-variant-outline"/></div>
              <div>{`You can always add rubrics later when you assign this scenario.`}</div>
            </div>}
          </div>
          <div>
            <label>Rubrics</label>
            {rubrics.length > 0 ? (
              <>
                <div className="rubric_grid">
                  {rubrics.map((rubric, index) => <RubricItem key={rubric._id || rubric.local_id} rubric={rubric} is_assessment={is_assessment} handleRemoveRubric={handleRemoveRubric} handleClickRubric={handleClickRubric} handleRubricSaved={handleRubricSaved} handleUnlinkRubric={handleUnlinkRubric} handleRelinkRubric={handleRelinkRubric}/>)}
                </div>
                <Spacing/>
              </>
            ) : (
              <>
                <div className="empty_rubric_list_placeholder">Please choose a rubric to enable AI feedback.</div>
                <HalfSpacing/>
              </>
            )}
            <div className="buttons">
              <Button text="Create or upload rubric" icon="plus" onClick={() => {setInRubricEditor({})}}/>
              <Button text="Choose a saved rubric" icon="folder-outline" onClick={handleBrowseRubrics}/>
            </div>
          </div>
          {suggested_rubrics.length > 0 && <div style={{maxWidth: "650px"}}>
            <Spacing/>
            <HalfSpacing/>
            <label>Suggested rubrics</label>
            <div className="scenario_list scenario_list_no_header" columns="1">
              {suggested_rubrics.map((file) => <SuggestedRubricItem key={file._id} file={file} handleAddSuggestedRubric={handleAddSuggestedRubric}/>)}
            </div>
          </div>}
        </div>
      </div>
    </div>
  )
}

function RubricItem({ rubric, is_assessment, handleRemoveRubric, handleClickRubric, handleRubricSaved, handleUnlinkRubric, handleRelinkRubric }) {

  const is_default = rubric._id?.startsWith("default");

  const { addToMyMenuItem = [] } = useSharedWithMe({ file: rubric.file, is_shared: rubric.file?._is_directly_shared, use_local_state: true, include_separator: false });

  const [openSaveToMyRubricsModal] = useModal(({ context, closeModal, setLoading }) => {
    function submitForm({ formatted_data, focusError }) {

      setLoading(true);

      let new_rubric = {
        data: {
          ...context.rubric.data,
          title: formatted_data.title
        }
      }

      publicAction("createOrUpdateRubric", {
        ...new_rubric,
        parent: formatted_data.parent?._id || null
      }).then(({ data }) => {

        closeModal();
        if (formatted_data.should_link) {
          handleRubricSaved({ rubric: context.rubric, new_rubric, id: data.id, version_id: data.version_id, file_id: data.file_id });
        }

      }).catch((error) => {
        console.error(error);
        setLoading(false);
        focusError({name: "title", error: "Something went wrong. Please try again."})
      })

    }

    return (
      <Modal title="Save to my rubrics">
        <FormProvider onSubmit={submitForm}>
          <FormSubmitProvider>
            {({handleSubmit}) => (
              <>
                <ModalContent>
                  <Textbox name="title" title="Rubric title" onEnter={handleSubmit} default_value={context.rubric.data.title || ""} autofocus={true} required={"Please enter a title for this rubric"}/>
                  <Spacing/>
                  <FileLocation name="parent" title="Choose where to save" type="rubric" />
                  {context.offer_to_link && <>
                    <Spacing/>
                    <Checkbox text={`Replace the rubric in this ${context.is_assessment ? "assessment" : "scenario"} with your new saved copy, so that the files are linked together.`} name="should_link" default_value={context.offer_to_link_default}/>
                  </>}
                </ModalContent>
                <ModalButtons>
                  <Button text="Cancel" className="hollow" onClick={closeModal}/>
                  <Button text="Save rubric" className="primary" onClick={handleSubmit}/>
                </ModalButtons>
              </>
            )}
          </FormSubmitProvider>
        </FormProvider>
      </Modal>
    )
  })

  const topMenuItems = [
    ...((rubric._id && ["view", "edit", "owner"].includes(rubric.file?._level) && !is_default) ? (
      addToMyMenuItem
    ) : [
      {
        label: 'Save to my rubrics',
        icon: 'bullseye-arrow-plus',
        onClick: () => {openSaveToMyRubricsModal({ rubric, offer_to_link: !rubric._id || !rubric.is_linked || is_default, offer_to_link_default: !rubric._id || is_default, is_assessment: is_assessment })},
      }
    ])
  ]

  const bottomMenuItems = [
    ...(rubric._id && !is_default ? [
      {
        label: 'Open original file',
        icon: 'open-in-new',
        onClick: () => {window.open(`/rubrics/${rubric._id}`, "_blank")},
        disabled: !["view", "edit", "owner"].includes(rubric.file?._level),
      },
    ] : [])
  ]

  const { handleOnClick, handleContextMenu } = useMenu({
    menuItems: [
      ...topMenuItems,
      ...(topMenuItems.length > 0 && bottomMenuItems.length > 0 ? ["separator"] : []),
      ...bottomMenuItems,
    ]
  })

  const { handleOnClick: handleOnClickDropdown } = useMenu({
    menuItems: rubric.is_linked ? [
      {
        heading: "Linked — Will update if original file is edited"
      },
      "separator",
      {
        label: 'Unlink',
        icon: 'link-off',
        onClick: () => {handleUnlinkRubric(rubric._id)}
      }
    ] : [
      {
        heading: `Unlinked — Edits will be just for this ${is_assessment ? "assessment" : "scenario"}`
      },
      "separator",
      {
        label: 'Relink and update',
        icon: 'link',
        disabled: !["view", "edit", "owner"].includes(rubric.file?._level),
        onClick: () => {handleRelinkRubric(rubric._id)}
      }
    ],
    rightHanded: false
  })

  return (
    <div className="rubric_card" onClick={() => {!rubric.is_relinking_loading && handleClickRubric(rubric)}} tabIndex={0} onContextMenu={handleContextMenu}>
      <div>
        <div>
          <div className={(rubric._id && !is_default) ? "rubric_red_icon" : ""}>
            <Icon name={is_default ? "sapient logo" : ((rubric._id || !rubric.data.title) ? "bullseye-arrow" : "bullseye-arrow-edit")}/>
          </div>
          <div>
            <div>{rubric.data.title ? rubric.data.title : <i>{`Rubric just for this ${(rubric.from_scenario || !is_assessment) ? "scenario" : "assessment"}`}</i>}</div>
            {is_assessment && rubric.from_scenario ? <div>{"From the scenario"}</div> : null}
          </div>
          <div>
            <RoundIconButton icon="dots-vertical" className="small" onClick={handleOnClick} />
            <RoundIconButton icon="close" className="small" onClick={(event) => {event.stopPropagation(); handleRemoveRubric(rubric)}} />
          </div>
        </div>
        <div>
          <RubricEditorTableThumbnail rows={convertSectionsToRows(rubric.data.sections)} levels={rubric.data.levels} scoring={rubric.data.scoring} />
          <div></div>
        </div>
        <div>
          {rubric._id ? (
            !is_default ? (
              rubric.is_relinking_loading ? (
                <Spinner className="rubric_card_spinner"/>
              ) : (
                <button className="rubric_card_dropdown" onClick={handleOnClickDropdown}>
                  <div><Icon name={rubric.is_linked ? "link" : "link-off"}/></div>
                  <div>{rubric.is_linked ? "Linked to original file" : "Unlinked"}</div>
                  <div><Icon name="menu-down-enlarged"/></div>
                </button>
              )
            ) : null
          ) : (
            rubric.data.title ? (
              <div className="rubric_card_dropdown">
                <div><Icon name="pencil-outline"/></div>
                <div>{`Edited just for this ${(rubric.from_scenario || !is_assessment) ? "scenario" : "assessment"}`}</div>
              </div>
            ) : (
              null
            )
          )}
        </div>
      </div>
    </div>
  )
}

function SuggestedRubricItem({ file, handleAddSuggestedRubric }) {
  const [is_loading, setLoading] = useState(false);

  const [openErrorModal] = useErrorModal();

  const handleAdd = () => {
    setLoading(true);
    publicAction("getRubricOverview", { id: file.item_id }).then(({ data }) => {
      if (data.value) {
        const { file, rubric } = data.value;
        handleAddSuggestedRubric({
          data: rubric.data,
          version_id: rubric.version_id,
          _id: rubric._id,
          file
        });
      } else {
        setLoading(false);
        openErrorModal();
      }
    }).catch((error) => {
      setLoading(false);
      console.error(error);
      openErrorModal();
    })
  }

  const is_default = file.item_id.startsWith("default");

  return (
    <div className="scenario_item" onClick={handleAdd} tabIndex={0}>
      <div className="item_icon">
        {is_loading ? (
          <Spinner className="scenario_item_spinner"/>
        ) : (
          is_default ? (
            <Icon name="sapient logo"/>
          ) : (
            <Icon name="bullseye-arrow" style={{fill: "var(--rubric_file_color)"}}/>
          )
        )}
      </div>
      <div className="item_title">{file.title}</div>
    </div>
  )
}

function Questions({visible}) {
  return (
    <div style={{display: (visible ? "contents" : "none")}} tabname="questions">
      <Header title="Follow-up questions"/>
      <div className="dashboard_content_inner nearest_scrollable_container" hidden={!visible}>
        <div className="dashboard_content_container">
          <div className="instructions_box">
            <div>
              <div><Icon name="information-slab-circle-outline"/></div>
              <div>You can ask the learner a few questions after the scenario ends to assess their understanding or present hypotheticals.</div>
            </div>
          </div>
          <TextboxList title="Questions" name="questions" add_button_label="Add question" multiline={true} height="short" reorderable={true} autofocus={visible}/>
        </div>
      </div>
    </div>
  )
}

function getLastTab(tab_groups, has_autofilled) {
  let starting_last_tab = tab_groups[0];
  let tabs = tab_groups[1];
  let ending_last_tab = tab_groups[2];

  let pending_tab = starting_last_tab;
  for (let tab of tabs) {
    if (!has_autofilled[tab]) {
      return pending_tab;
    }
    pending_tab = tab;
  }

  return ending_last_tab;
}